import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

import * as Sentry from '@sentry/angular';
import { i18n } from './i18n';
import { isOAuthAuthorizationPath } from './app/pages/oauth-authorization/oauth-authorization-route';
import { buildWebSentryConfig } from './sentry-config';

const serverErrorsRegexArray = [
	/0 Unknown Error/,
	/403 Forbidden/,
	/Http failure response/,
	/500 Internal Server Error/,
	/401 Unauthorized/,
	/404 Not Found/,
	/502 Bad Gateway/,
	/503 Service Unavailable/,
	/AbortError/
];

// Noise from browser extensions (e.g. MetaMask / crypto wallets) that inject scripts into
// the page. These errors originate in extension code, not our app, so they are not actionable.
const browserExtensionErrorsRegexArray = [/Failed to connect to MetaMask/, /MetaMask/, /window\.ethereum/];

// URLs that indicate the error came from a browser-extension context rather than our bundle.
const browserExtensionUrlDenylist = [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-extension:\/\//];

function isFromBrowserExtension(event: Sentry.Event): boolean {
	const frames = event.exception?.values?.flatMap(value => value.stacktrace?.frames ?? []) ?? [];
	return frames.some(frame => {
		const filename = frame.filename ?? '';
		return browserExtensionUrlDenylist.some(match => match.test(filename));
	});
}

const sentryConfig = buildWebSentryConfig(environment);
if (sentryConfig.enabled && !isOAuthAuthorizationPath(window.location.pathname)) {
	Sentry.init({
		dsn: sentryConfig.dsn,
		environment: sentryConfig.environment,
		release: sentryConfig.release,

		integrations: [
			Sentry.browserTracingIntegration(),
			...(sentryConfig.replayEnabled
				? [
						Sentry.replayIntegration({
							maskAllText: true,
							blockAllMedia: true,
							networkDetailAllowUrls: [/\.herokuapp\.com/, /\.vmlsites\.com/],
							networkCaptureBodies: false,
							networkRequestHeaders: ['Content-Type']
						})
				  ]
				: []),
			// autoInject:false suppresses the default floating widget; the ad-editor
			// agent sidebar opens the dialog programmatically via createForm() so
			// per-click tags can carry the live template + conversation ids.
			Sentry.feedbackAsyncIntegration({
				autoInject: false,
				colorScheme: 'system',
				messagePlaceholder: "Describe the issue. Please don't include sensitive customer data or PII."
			})
		],

		tracesSampleRate: sentryConfig.traceSampleRate,
		tracePropagationTargets: ['localhost', /\.herokuapp\.com/, /\bcce\.vmlsites\.com/],

		replaysSessionSampleRate: sentryConfig.replaySessionSampleRate,
		replaysOnErrorSampleRate: sentryConfig.replayOnErrorSampleRate,

		ignoreErrors: [...serverErrorsRegexArray, ...browserExtensionErrorsRegexArray],

		beforeSend(event, hint) {
			// Drop events that originate from browser-extension code (e.g. wallet extensions).
			if (isFromBrowserExtension(event)) {
				return null;
			}

			const originalException = hint?.originalException;
			if (!originalException) {
				return event;
			}

			const error =
				typeof originalException === 'string'
					? originalException
					: (originalException as Error)?.message || String(originalException);

			const isServerError = serverErrorsRegexArray.some(match => match.test(error));

			if (error && error !== '[object Object]' && !isServerError) {
				return event;
			}

			return null;
		}
	});
}

if (environment.production) {
	enableProdMode();
}

i18n.getTranslationProviders()
	.then(providers => {
		platformBrowserDynamic()
			.bootstrapModule(AppModule)
			.catch(err => console.error(err));
	})
	.catch(err => {
		console.error(err);
		alert('Error loading translation files. Please try again later.');
	});
